import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Volume2, Pause, Play, Loader2, CheckCircle2, Radio, AlertTriangle, Headphones, Brain, ThumbsUp, HelpCircle } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const LANG_LABEL = { ta: 'தமிழ்', hi: 'हिन्दी', ne: 'नेपाली', kn: 'ಕನ್ನಡ', en: 'English' };

/**
 * Public Radio Listen page — opened via WhatsApp tokenized link.
 *
 * NO LOGIN REQUIRED. The token in the URL grants single-purpose access to
 * exactly one SOP+language for one staff member. Auto-logs "opened",
 * auto-logs "completed" when audio ends.
 *
 * Mobile-first: big play button, minimal chrome, readable on a 5" screen
 * on 2G, listens in staff's mother tongue.
 */
export default function RadioListenPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [playerState, setPlayerState] = useState('idle'); // idle | loading | playing | ended | error
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = new Audio();

  useEffect(() => {
    axios.get(`${API}/api/r/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'This training link is invalid or expired.'));
  }, [token]);

  useEffect(() => {
    const a = audioRef.current;
    const onEnded = () => {
      setPlayerState('ended');
      axios.post(`${API}/api/r/${token}/track`, { event: 'completed' }).catch(() => {});
    };
    const onError = () => setPlayerState('error');
    a.addEventListener('ended', onEnded);
    a.addEventListener('error', onError);
    return () => {
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('error', onError);
      try { a.pause(); } catch (_) { /* no-op */ }
    };
  }, [token]);

  const play = async () => {
    const a = audioRef.current;
    if (playerState === 'playing') {
      a.pause();
      setPlayerState('idle');
      return;
    }
    setPlayerState('loading');
    try {
      if (!a.src) a.src = `${API}/api/r/${token}/audio`;
      await a.play();
      setPlayerState('playing');
      axios.post(`${API}/api/r/${token}/track`, { event: 'played' }).catch(() => {});
    } catch (e) {
      setPlayerState('error');
    }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#F7F5F2' }}>
        <div className="max-w-sm w-full bg-white rounded-2xl border p-6 text-center" style={{ borderColor: '#E5E1DC' }}>
          <AlertTriangle size={32} className="mx-auto mb-3" style={{ color: '#D4726A' }} />
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Link not available</p>
          <p className="text-xs mt-2" style={{ color: '#6B7280' }}>{err}</p>
          <p className="text-[11px] mt-4" style={{ color: '#9CA3AF' }}>Please ask your supervisor for a fresh link.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F5F2' }}>
        <Loader2 size={28} className="animate-spin" style={{ color: '#2A7B88' }} />
      </div>
    );
  }

  const greetings = {
    ta: `வணக்கம், ${data.person_name}!`,
    hi: `नमस्ते, ${data.person_name}!`,
    ne: `नमस्ते, ${data.person_name}!`,
    kn: `ನಮಸ್ಕಾರ, ${data.person_name}!`,
    en: `Hi ${data.person_name}!`,
  };
  const greet = greetings[data.lang] || greetings.en;

  // Block common "save the text" keyboard shortcuts at the page level.
  // Deliberately soft — doesn't try to stop devtools (users who want to
  // extract text via F12 can still do so) but blocks the 99% case where
  // someone instinctively hits Ctrl+A / Ctrl+C or right-clicks.
  const blockKeyboardCopy = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const k = e.key?.toLowerCase();
    if (['a', 'c', 'x', 's', 'p'].includes(k)) {
      e.preventDefault();
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: '#F7F5F2' }}
      data-testid="radio-listen-page"
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={blockKeyboardCopy}
      tabIndex={-1}
    >
      {/* Header */}
      <div className="px-5 pt-6 pb-8" style={{ background: 'linear-gradient(180deg, #2A7B88 0%, #1B5A66 100%)' }}>
        <div className="max-w-md mx-auto">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full mb-3"
               style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>
            <Radio size={11} /> ScaleBoard Radio
          </div>
          <p className="text-base text-white/90" data-testid="radio-greeting">{greet}</p>
          <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {LANG_LABEL[data.lang] || data.lang} · {data.has_audio ? '2-min training' : 'Audio being prepared'}
          </p>
        </div>
      </div>

      {/* Player card */}
      <div className="max-w-md mx-auto px-4 -mt-6">
        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-lg font-bold leading-snug" style={{ color: '#1B2A4A' }} data-testid="radio-sop-title">
            {data.sop_title}
          </p>

          {/* Play button */}
          {data.has_audio ? (
            <button onClick={play} className="w-full mt-5 py-5 rounded-xl transition flex items-center justify-center gap-2 font-semibold"
              style={{
                backgroundColor: playerState === 'playing' ? '#F5C86B' : playerState === 'ended' ? '#ECFDF5' : '#2A7B88',
                color: playerState === 'playing' ? '#8C560F' : playerState === 'ended' ? '#166534' : '#FFFFFF',
                boxShadow: '0 4px 12px rgba(42,123,136,0.25)',
              }}
              data-testid="radio-play-btn"
            >
              {playerState === 'loading' ? <><Loader2 size={20} className="animate-spin" /> Loading…</>
                : playerState === 'playing' ? <><Pause size={22} /> Pause</>
                : playerState === 'ended' ? <><CheckCircle2 size={22} /> Listened — thank you!</>
                : playerState === 'error' ? <><AlertTriangle size={20} /> Tap to retry</>
                : <><Play size={22} /> Listen now</>}
            </button>
          ) : (
            <div className="mt-5 rounded-xl p-4 text-center" style={{ backgroundColor: '#FEF3C7' }}>
              <Headphones size={18} className="mx-auto mb-1" style={{ color: '#BA7517' }} />
              <p className="text-xs font-semibold" style={{ color: '#8C560F' }}>Audio being prepared</p>
              <p className="text-[11px] mt-1" style={{ color: '#BA7517' }}>You'll get a fresh link once the recording is ready.</p>
            </div>
          )}

          {/* Transcript / text fallback — copy-protected.
              Staff can READ along to the audio, but the text can't be
              copy/pasted, right-clicked, or drag-selected. This is
              discouragement-grade (a determined user with devtools can still
              extract it) but stops casual copy/paste sharing of proprietary
              training content. */}
          {data.content && (
            <div className="mt-5 border-t pt-4" style={{ borderColor: '#F3F0EB' }}>
              <p className="text-[11px] uppercase tracking-wide font-semibold mb-2" style={{ color: '#9CA3AF' }}>
                Read along
              </p>
              <div
                className="text-sm whitespace-pre-wrap leading-relaxed select-none"
                style={{
                  color: '#1B2A4A',
                  fontFamily: 'Noto Sans, system-ui',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  userSelect: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onContextMenu={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                data-testid="radio-content-protected"
              >
                {data.content}
              </div>
              <p className="mt-2 text-[10px]" style={{ color: '#C7BBA4' }}>
                © ScaleBoard · Proprietary training content. Please don't copy or share externally.
              </p>
            </div>
          )}

          {/* Interactive Check Questions */}
          {data.check_questions && data.check_questions.length > 0 && (
            <CheckQuestionsQuiz
              questions={data.check_questions}
              token={token}
            />
          )}
        </div>

        <p className="text-center text-[10px] mt-6 pb-6" style={{ color: '#9CA3AF' }}>
          🔒 This training is confidential · do not forward this link
        </p>
      </div>
    </div>
  );
}


/**
 * Interactive Check Questions — tappable cards.
 *
 * Each question becomes a card. Staff taps "I know this ✓" or "Tricky 🤔".
 * Once all questions are tapped, logs "quiz_completed" event. Answers are
 * self-reported (no right/wrong) — the goal is active reflection, not
 * assessment. Builds a habit of stopping to think after listening.
 */
function CheckQuestionsQuiz({ questions, token }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const answer = (idx, choice) => {
    setAnswers((prev) => {
      if (prev[idx] !== undefined) return prev; // one-shot
      return { ...prev, [idx]: choice };
    });
  };

  const allAnswered = Object.keys(answers).length >= questions.length;

  useEffect(() => {
    if (allAnswered && !submitted) {
      setSubmitted(true);
      // Summarise — confident count vs tricky count
      const trickyCount = Object.values(answers).filter((v) => v === 'tricky').length;
      axios.post(`${API}/api/r/${token}/track`, {
        event: 'quiz_completed',
        meta: { total: questions.length, tricky: trickyCount },
      }).catch(() => {});
    }
  }, [allAnswered, submitted, answers, questions.length, token]);

  return (
    <div className="mt-6 border-t pt-4" style={{ borderColor: '#F3F0EB' }} data-testid="check-questions-quiz">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F5C86B' }}>
          <Brain size={13} style={{ color: '#8C560F' }} />
        </div>
        <div>
          <p className="text-xs font-bold" style={{ color: '#1B2A4A' }}>Check your understanding</p>
          <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Tap your confidence level. No wrong answers.</p>
        </div>
      </div>

      <div className="space-y-2">
        {questions.map((q, idx) => {
          const chosen = answers[idx];
          return (
            <div key={idx} className="rounded-xl border p-3" style={{ borderColor: chosen ? '#ECFDF5' : '#E5E1DC', backgroundColor: chosen ? '#F9FDFB' : '#FFF' }}>
              <p className="text-sm font-medium mb-2" style={{ color: '#1B2A4A' }} data-testid={`quiz-q-${idx}`}>
                <span style={{ color: '#9CA3AF' }}>Q{idx + 1}.</span> {q}
              </p>
              {!chosen ? (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => answer(idx, 'confident')}
                    className="flex-1 py-2 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1"
                    style={{ backgroundColor: '#ECFDF5', color: '#166534', border: '1px solid #86EFAC' }}
                    data-testid={`quiz-q-${idx}-confident`}
                  >
                    <ThumbsUp size={12} /> I know this
                  </button>
                  <button onClick={() => answer(idx, 'tricky')}
                    className="flex-1 py-2 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1"
                    style={{ backgroundColor: '#FFFBEB', color: '#8C560F', border: '1px solid #F5C86B' }}
                    data-testid={`quiz-q-${idx}-tricky`}
                  >
                    <HelpCircle size={12} /> Tricky
                  </button>
                </div>
              ) : (
                <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold"
                     style={{ color: chosen === 'confident' ? '#166534' : '#8C560F' }}>
                  {chosen === 'confident' ? <><CheckCircle2 size={12} /> Got it</> : <><HelpCircle size={12} /> Noted — we'll cover it again</>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {submitted && (
        <div className="mt-4 text-center py-3 rounded-xl" style={{ backgroundColor: '#ECFDF5', border: '1px solid #86EFAC' }}>
          <CheckCircle2 size={22} className="mx-auto mb-1" style={{ color: '#166534' }} />
          <p className="text-xs font-bold" style={{ color: '#166534' }}>Thank you for reflecting!</p>
          <p className="text-[10px] mt-0.5" style={{ color: '#166534' }}>Your manager can see you engaged with this training.</p>
        </div>
      )}
    </div>
  );
}
